<script setup lang="ts">
/**
 * 应用外壳：只做组装与全局交互。
 * 注意 CanvasStage 是子组件 —— 它的 onMounted（引擎挂载）先于本组件的 onMounted（载入文档）执行。
 */
import { onBeforeUnmount, onMounted, watch } from 'vue';
import CanvasStage from '@/components/CanvasStage.vue';
import CodePanel from '@/components/CodePanel.vue';
import InspectorTabs from '@/components/Inspector/InspectorTabs.vue';
import LayerList from '@/components/LayerList.vue';
import StatusBar from '@/components/StatusBar.vue';
import { useEditor } from '@/composables/useEditor';
import { loadLayout, ui } from '@/stores/editor';
import { useHotkeys } from '@/composables/useHotkeys';
import { attachEmbed, isEmbedded, readEmbedOptions, type AttachedEmbed } from '@/embed';
import { EMPTY_DOC } from '@/demo';

const editor = useEditor();

/**
 * 宿主嵌入（`?embed=1`）：宿主在 el-dialog 里用 <iframe> 装这个编辑器，
 * 只做两件事 —— 「打开这份 SVG / 拿回改完的 SVG 存回去」（协议见 docs/EMBED.md）。
 * 桥只在**真的被 iframe 嵌套**且带 `?embed=1` 时启用，顶层打开零影响。
 */
let embed: AttachedEmbed | null = null;

// 文档变更 / 脏状态翻转 → 主动推给宿主（宿主据此决定「确定」按钮能不能点）
watch([() => editor.version.value, () => editor.pristine.value], () => embed?.bridge.notifyChange());

useHotkeys({
  undo: editor.undo,
  redo: editor.redo,
  zoomFit: editor.zoomFit,
  zoomToSelection: editor.zoomToSelection,
  setZoom: editor.setZoom,
  clearSelection: editor.clearSelection,
  deleteSelection: editor.deleteSelection,
  selectAll: editor.selectAll,
  group: editor.groupSelection,
  ungroup: editor.ungroupSelection,
  duplicate: editor.duplicateSelection,
  reorder: editor.reorderSelection,
  nudge: editor.nudgeSelection,
  exitContext: editor.exitGroupContext,
  copy: editor.copySelection,
  cut: editor.cutSelection,
  paste: editor.pasteClipboard,
  editText: editor.beginTextEdit,
  isEditingText: () => editor.textEditing.value !== null,
  setTool: (tool) => {
    ui.tool = tool;
  },
  exportSvg: () => editor.download(),
  exportProject: () => editor.exportProject(),
  openFromClipboard: () => void openFromClipboard(),
  toggleCode: () => {
    ui.codeOpen = !ui.codeOpen;
  },
});

/** Ctrl+Shift+V：把系统剪贴板里的 SVG 文本当新文档打开 */
const openFromClipboard = async (): Promise<void> => {
  try {
    const text = await navigator.clipboard.readText();
    if (!editor.openText(text, null)) ui.status = '剪贴板里没有可用的 SVG';
  } catch {
    ui.status = '读不到剪贴板（浏览器未授权）';
  }
};

onMounted(() => {
  loadLayout(); // 分栏宽度从 localStorage 恢复
  // 进入即**空白画板**（不塞示例内容）：用户要的是干净的起点。
  // 示例文档仍在 demo.ts 里（DEMO_SVG），需要时可由状态栏或调试入口载入。
  editor.loadText(EMPTY_DOC);

  // 宿主嵌入：顶层打开只带 ?embed=1 时静默忽略（不报错、不改任何行为）
  const { enabled, anyOrigin } = readEmbedOptions(window.location.search);
  if (!enabled || !isEmbedded()) return;
  embed = attachEmbed(
    {
      getSvgText: () => editor.exportText(),
      loadSvgText: (text, name) => editor.openText(text, name),
      isDirty: () => !editor.pristine.value,
      canUndo: () => editor.canUndo.value,
      canRedo: () => editor.canRedo.value,
      docName: () => editor.sourceName.value,
    },
    { anyOrigin },
  );
  // 宿主收到 ready 才会发 load —— iframe 的 onload 早于 Vue 挂载，靠这条消息对齐时序
  embed.bridge.notifyReady();
});

onBeforeUnmount(() => {
  embed?.detach();
  embed = null;
});

/** 拖进来的可能是工程文件（.svgedit.json）或 SVG */
const hasFiles = (e: DragEvent): boolean => Array.from(e.dataTransfer?.types ?? []).includes('Files');

/* 进入/离开成对计数：dragenter/dragleave 会随子元素来回冒泡，单看一次会闪 */
let dragDepth = 0;

const onDragEnter = (e: DragEvent): void => {
  if (!hasFiles(e)) return;
  dragDepth += 1;
  ui.dragOver = true;
};

const onDragLeave = (e: DragEvent): void => {
  if (!hasFiles(e)) return;
  dragDepth = Math.max(0, dragDepth - 1);
  if (dragDepth === 0) ui.dragOver = false;
};

const onDrop = async (e: DragEvent): Promise<void> => {
  dragDepth = 0;
  ui.dragOver = false;
  const file = e.dataTransfer?.files?.[0];
  if (!file) return;
  // 拖进来的可能是工程文件（.svgedit.json）或 SVG
  if (/\.svgedit\.json$|\.json$/i.test(file.name)) await editor.openProjectFile(file);
  else await editor.openFile(file);
};
</script>

<template>
  <div
    class="app"
    :style="{ '--list-w': `${ui.listW}px`, '--insp-w': `${ui.inspW}px` }"
    @dragenter.prevent="onDragEnter"
    @dragover.prevent
    @dragleave="onDragLeave"
    @drop.prevent="onDrop"
  >
    <LayerList />
    <CanvasStage />
    <InspectorTabs />
    <StatusBar />
    <CodePanel />
  </div>
</template>
